import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Filters from '@/components/UI/Filters';
import Icon from '@/components/UI/Icon';
import Label from '@/components/UI/Label';
import Link from '@/components/UI/Link';
import SearchFilterBar from '@/components/UI/SearchFilterBar';
import TableFilterMenu from '@/components/UI/SelectDropDown/Menus/TableFilterMenu';
import Table from '@/components/UI/Table';
import { filterDataByFilters, filterDataBySearch } from '@/components/UI/Table/Table.utils.ts';
import UserAvatar from '@/components/UI/UserAvatar';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { returnUserFromStudy } from '@/helpers';
import { _isEmpty, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { web as webRoute } from '@/support/route';
import { sortDescByProperty } from '@/utils/array.utils';
import { DateUtils } from '@/utils/Date.utils';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import './TaskTable.scss';
import { LabelType } from '@/components/UI/Label/Label.tsx';
import { type TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';

interface TaskUser {
  id: string;
  name: string;
  email: string;
}

interface TaskStudy {
  id: string;
  name: string;
  archived_at?: string | null;
  canceled_at?: string | null;
}

interface Task {
  id: string;
  description: string;
  status: 'incomplete' | 'in_progress' | 'complete';
  task_template_id: string | null;
  last_updated_by: TaskUser;
  last_updated_at: string;
  study: TaskStudy;
}

interface TaskTableProps {
  tasks: Array<Task>;
  updateTask: (task: Task) => void;
  deleteTask: (taskId: string) => void;
}

const status = [
  {
    value: 'incomplete',
    name: 'Incomplete',
  },
  {
    value: 'in_progress',
    name: 'In progress',
  },
  {
    value: 'complete',
    name: 'Completed',
  },
];

const filterOptions = [
  {
    value: 'status',
    name: 'Status',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: status,
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: status,
      },
    ],
  },
];

const getTrafficLight = (status: Task['status']) => {
  switch (status) {
    case 'complete': {
      return { class: 'green', label: 'Complete' };
    }
    case 'in_progress': {
      return { class: 'yellow', label: 'In progress' };
    }
    case 'incomplete': {
      return { class: 'red', label: 'Incomplete' };
    }
    default: {
      return { class: 'red', label: 'Incomplete' };
    }
  }
};

const TaskTable: FC<TaskTableProps> = ({ tasks, updateTask, deleteTask }) => {
  const [settings, setSettings] = useState({
    rows: 25,
    page: 0,
  });
  const [searchQuery, setQuery] = useState('');
  const [filterMatch, setFilterMatch] = useState(true);
  const [filters, setFilters] = useState<Record<string, any>>({});

  const dispatch = useDispatch();

  const data = useMemo(() => {
    let result = [...(tasks ?? [])];

    if (_isNotEmpty(filters)) {
      result = filterDataByFilters(result.sort(sortDescByProperty('id')), filters, filterMatch, filterOptions);
    }
    if (_isNotBlank(searchQuery)) {
      result = filterDataBySearch(result, searchQuery);
    }
    return result;
  }, [tasks, filters, searchQuery, filterMatch]);

  const onUpdateFilters = useCallback(
    (filters: Record<string, any> | undefined, filterMatch: boolean) => {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      setFilters(filters || {});
      setFilterMatch(filterMatch);
      if (!_isEmpty(filters) && settings.page !== 0) {
        const currentSettings = { ...settings };
        currentSettings.page = 0;
        setSettings(currentSettings);
      }
    },
    [settings]
  );

  const openModal = useCallback(
    (modal: string, props: Record<string, unknown>) => {
      dispatch({
        type: 'OPEN_MODAL',
        modal,
        props,
      });
    },
    [dispatch]
  );

  const closeModal = useCallback(() => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  }, [dispatch]);

  const Filter = () => (
    <TableFilterMenu
      filterOptions={filterOptions}
      filters={filters}
      onUpdateFilters={onUpdateFilters}
      filterMatch={filterMatch}
    />
  );

  const columns: Array<TanStackColumnDef<Task>> = useMemo(
    () => [
      {
        accessorKey: 'description',
        id: 'task',
        header: 'Task',
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: Task } }) => (
          <div className="flex flex-wrap items-center">
            {original.task_template_id !== null && (
              <span
                data-tooltip-id="global-tooltip-id"
                data-tooltip-content="Generated by a template"
                className="mr3 dib gray relative"
                style={{ top: '2px' }}
              >
                <Icon icon="tray" width="15" height="15" viewBox="0 0 43 32" />
              </span>
            )}
            <a
              className={'link blue'}
              onClick={() =>
                openModal('VIEW_TASK', {
                  task: original,
                  updateTask: updateTask,
                  openModal: openModal,
                  deleteTask: deleteTask,
                  closeModal,
                })
              }
            >
              {original.description}
            </a>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        id: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: Task } }) => {
          const trafficLight = getTrafficLight(original.status);
          return (
            <div style={{ minWidth: '150px' }} className={`f6 pa2 br2 text-black ${trafficLight.class}`}>
              {trafficLight.label}
            </div>
          );
        },
      },
      {
        accessorFn: (row: Task) => row.last_updated_by?.name,
        id: 'assigned',
        header: 'Assigned',
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: Task } }) => {
          const user = returnUserFromStudy(original.study, original.last_updated_by?.id);
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (user) {
            return <UserAvatar user={user} tooltip={user.name ?? user.email} />;
          }
          return false;
        },
      },
      {
        accessorKey: 'last_updated_at',
        id: 'updatedAt',
        header: 'Updated at',
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: Task } }) => (
          <DateTimeRenderer value={original.last_updated_at} defaultResponse="-" />
        ),
      },
      {
        id: 'study',
        header: 'Study',
        accessorFn: (row: Task) => row.study?.name,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: Task } }) => (
          <span>
            <Link
              className="link blue"
              style={{ whiteSpace: 'normal' }}
              to={webRoute('studies.show', { id: original.study?.id ?? '' })}
            >
              {original.study?.name}
            </Link>
            {_notNil(original.study?.archived_at) ? (
              <Label
                text="Completed"
                tooltip={`This study was completed on ${DateUtils.renderDateTime(original.study?.archived_at)}`}
                type={LabelType.Info}
                className="ml2"
              />
            ) : _notNil(original.study?.canceled_at) ? (
              <Label
                text="Cancelled"
                tooltip={`This study was cancelled on ${DateUtils.renderDateTime(original.study?.canceled_at)}`}
                type={LabelType.Info}
                className="ml2"
              />
            ) : undefined}
          </span>
        ),
      },
    ],
    [openModal, updateTask, deleteTask, closeModal]
  );

  return (
    <div className="ui-card">
      <div className="pa3">
        <SearchFilterBar
          FilterMenu={Filter}
          searchPlaceholderText="Search by task"
          handleSearchInput={setQuery}
          searchQuery={searchQuery}
        />
        {!_isEmpty(filters) && <Filters filters={filters} updateFilters={onUpdateFilters} filterMatch={filterMatch} />}
      </div>
      <div className="ui__table ui__tasks-table">
        <Table data={data} columns={columns} />
      </div>
    </div>
  );
};
export default TaskTable;
